import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Activity } from 'lucide-react';
import _ from 'lodash';

// Memoized Slider component
const Slider = React.memo(({ label, value, onChange, max = 10000 }) => (
  <div className="mb-4">
    <div className="flex justify-between mb-2">
      <label className="text-sm font-medium">{label}</label>
      <span className="text-sm font-medium">${value}</span>
    </div>
    <input
      type="range"
      min="0"
      max={max}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
    />
  </div>
));

// Constants moved outside component
const COLORS = ['#4F46E5', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4'];
const EXPENSE_GROUPS = {
  Housing: ['rent', 'utilities', 'insurance', 'maintenance'],
  Transportation: ['carPayment', 'gasoline', 'carInsurance', 'carMaintenance'],
  Living: ['groceries', 'dining', 'healthcare', 'clothing'],
  Entertainment: ['streaming', 'hobbies', 'travel'],
  Financial: ['savings', 'investments', 'debt', 'emergency']
};

const BudgetCalculator = () => {
  const [income, setIncome] = useState({
    salary: 5000,
    investments: 500,
    sideHustle: 1000,
    other: 200
  });

  const [expenses, setExpenses] = useState({
    rent: 1500, utilities: 200, insurance: 100, maintenance: 100,
    carPayment: 300, gasoline: 150, carInsurance: 100, carMaintenance: 100,
    groceries: 400, dining: 200, healthcare: 200, clothing: 100,
    streaming: 50, hobbies: 100, travel: 200,
    savings: 500, investments: 300, debt: 200, emergency: 100
  });

  const [sliderIncome, setSliderIncome] = useState(income);
  const [sliderExpenses, setSliderExpenses] = useState(expenses);
  const [animatedHealth, setAnimatedHealth] = useState(0);
  const [chartKey, setChartKey] = useState(0);

  // Memoized calculations
  const { totalIncome, totalExpenses, budgetHealth } = useMemo(() => ({
    totalIncome: Object.values(sliderIncome).reduce((a, b) => a + b, 0),
    totalExpenses: Object.values(sliderExpenses).reduce((a, b) => a + b, 0),
    budgetHealth: ((Object.values(sliderIncome).reduce((a, b) => a + b, 0) - 
                   Object.values(sliderExpenses).reduce((a, b) => a + b, 0)) / 
                   Object.values(sliderIncome).reduce((a, b) => a + b, 0)) * 100
  }), [sliderIncome, sliderExpenses]);

  const groupTotals = useMemo(() => 
    Object.entries(EXPENSE_GROUPS).reduce((acc, [group, items]) => {
      acc[group] = items.reduce((sum, item) => sum + sliderExpenses[item], 0);
      return acc;
    }, {}),
    [sliderExpenses]
  );

  const expenseData = useMemo(() => 
    Object.entries(groupTotals).map(([name, value]) => ({ name, value })),
    [groupTotals]
  );

  const incomeData = useMemo(() => 
    Object.entries(sliderIncome).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    })),
    [sliderIncome]
  );

  const monthlyData = useMemo(() => 
    Array.from({ length: 12 }, (_, i) => ({
      month: `Month ${i + 1}`,
      income: totalIncome,
      expenses: totalExpenses
    })),
    [totalIncome, totalExpenses]
  );

  // Debounced setters with cleanup
  const debouncedSetIncome = useCallback(
    _.debounce((newValues) => {
      setIncome(newValues);
      setChartKey(prev => prev + 1);
    }, 300),
    []
  );

  const debouncedSetExpenses = useCallback(
    _.debounce((newValues) => {
      setExpenses(newValues);
      setChartKey(prev => prev + 1);
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetIncome.cancel();
      debouncedSetExpenses.cancel();
    };
  }, [debouncedSetIncome, debouncedSetExpenses]);

  const handleSliderChange = useCallback((category, type, value) => {
    const numValue = Number(value);
    if (type === 'income') {
      const newIncome = { ...sliderIncome, [category]: numValue };
      setSliderIncome(newIncome);
      debouncedSetIncome(newIncome);
    } else {
      const newExpenses = { ...sliderExpenses, [category]: numValue };
      setSliderExpenses(newExpenses);
      debouncedSetExpenses(newExpenses);
    }
  }, [sliderIncome, sliderExpenses, debouncedSetIncome, debouncedSetExpenses]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedHealth(budgetHealth);
    }, 100);
    return () => clearTimeout(timer);
  }, [budgetHealth]);


  return (
    <div className="space-y-6 p-6 bg-gray-50 rounded-lg">
      <h1 className="text-3xl font-bold mb-6">Budget Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Income Section */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Income</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(sliderIncome).map(([category, value]) => (
              <Slider
                key={category}
                label={category.charAt(0).toUpperCase() + category.slice(1)}
                value={value}
                onChange={(val) => handleSliderChange(category, 'income', val)}
              />
            ))}
            <div className="mt-4 text-lg font-semibold">
              Total Income: ${totalIncome}
            </div>
          </CardContent>
        </Card>

        {/* Expenses Section */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Expenses</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(EXPENSE_GROUPS).map(([group, categories]) => (
              <div key={group}>
                <h3 className="text-lg font-semibold mb-4">{group}</h3>
                {categories.map(category => (
                  <Slider
                    key={category}
                    label={category.charAt(0).toUpperCase() + category.slice(1)}
                    value={sliderExpenses[category]}
                    onChange={(val) => handleSliderChange(category, 'expenses', val)}
                  />
                ))}
                <div className="mt-2 text-sm font-medium text-gray-600">
                  Subtotal: ${groupTotals[group]}
                </div>
              </div>
            ))}
            <div className="mt-4 text-lg font-semibold">
              Total Expenses: ${totalExpenses}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Budget Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center mb-6">
            <div className="text-4xl font-bold" style={{
              color: animatedHealth > 20 ? '#10B981' : '#EF4444'
            }}>
              {animatedHealth.toFixed(1)}%
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Expense Distribution */}
            <div className="h-64">
              <h3 className="text-center mb-4">Expense Distribution</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart key={`expense-${chartKey}`}>
                  <Pie
                    data={expenseData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {expenseData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Income Distribution */}
            <div className="h-64">
              <h3 className="text-center mb-4">Income Distribution</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart key={`income-${chartKey}`}>
                  <Pie
                    data={incomeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {incomeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="mt-6 h-64">
            <h3 className="text-center mb-4">Monthly Income vs Expenses</h3>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData} key={`line-${chartKey}`}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend 
                  layout="vertical" 
                  align="right" 
                  verticalAlign="middle"
                  />
                <Line 
                  type="monotone" 
                  dataKey="income" 
                  stroke="#10B981" 
                  activeDot={{ r: 8 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="expenses" 
                  stroke="#EF4444"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BudgetCalculator;